import { useState } from 'react';
import { Alert, Button, Divider, Flex, Form, Input, Typography, theme } from 'antd';
import { Link, useNavigate, useSearchParams } from 'react-router';
import {
  ContinuarComGoogle,
  EtiquetaDaUltimaVez,
  formaDeEntradaAnterior,
  lembrarFormaDeEntrada,
  MENSAGEM_DE_FALHA_INESPERADA,
  mensagemDeErro,
  mensagemDoRetornoOAuth,
  TituloDaTela,
} from '@titularis/ui';
import { authClient, signIn } from '../../auth';
import { ReenviarVerificacao } from './ReenviarVerificacao';
import { comDestino, useDestino } from './destino';
import { retornoDoEmail } from './verificacao';

interface Campos {
  email: string;
  password: string;
}

/**
 * A tela de entrada.
 *
 * A ordem das duas portas é deliberada, e é a da Voa: **o Google primeiro, a
 * senha depois da linha**. Quem chega aqui vem de um link de WhatsApp ou do
 * botão da landing e muitas vezes não sabe se já tem conta - e o Google
 * responde às duas situações com o mesmo toque, porque entra em conta existente
 * e cria a que não existe. O formulário embaixo é para quem já escolheu senha.
 */
export function LoginPage() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const destino = useDestino();
  const [busca] = useSearchParams();
  const [erro, setErro] = useState<string | null>(() => mensagemDoRetornoOAuth(busca.get('error')));
  const [naoVerificado, setNaoVerificado] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  /*
   * Lido uma vez, na montagem: se fosse lido a cada render, a etiqueta pularia
   * do Google para o e-mail no instante entre gravar a escolha e a página sair
   * do ar.
   */
  const [anterior] = useState(formaDeEntradaAnterior);

  const entrar = async ({ email, password }: Campos) => {
    setErro(null);
    setNaoVerificado(null);
    setEnviando(true);

    try {
      const { error } = await signIn.email({ email, password });

      if (error) {
        // A API distingue e-mail não verificado das demais falhas; o aluno
        // precisa saber qual dos dois é para saber o que fazer — e, no
        // primeiro caso, ter como pedir um novo link.
        setErro(mensagemDeErro(error, 'E-mail ou senha incorretos.'));
        if (error.code === 'EMAIL_NOT_VERIFIED') setNaoVerificado(email);
        return;
      }

      lembrarFormaDeEntrada('email');

      // Onde a pessoa queria chegar, e não a Home: quem veio da landing
      // clicando em "Comprar" chega aqui com `?destino=/planos`.
      void navigate(destino, { replace: true });
    } catch {
      setErro(MENSAGEM_DE_FALHA_INESPERADA);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Flex vertical gap={token.margin}>
      <TituloDaTela
        titulo="Bom te ver por aqui"
        descricao="Entre para continuar seus estudos para Titular de Cartório."
      />

      {erro ? <Alert type="error" showIcon title={erro} /> : null}
      {naoVerificado ? (
        <ReenviarVerificacao
          email={naoVerificado}
          retorno={retornoDoEmail(destino)}
          // Com o link novo a caminho, o erro que trouxe a pessoa até aqui já
          // não descreve o estado dela: some, para sobrar só o "e-mail enviado".
          aoEnviar={() => setErro(null)}
        />
      ) : null}

      <ContinuarComGoogle
        authClient={authClient}
        destino={destino}
        destaque={anterior === 'google'}
        aoFalhar={(mensagem) => setErro(mensagem || null)}
      />

      {/* A linha do "ou" separa as duas portas sem hierarquizá-las. */}
      <Divider plain style={{ margin: 0 }}>
        <Typography.Text type="secondary">ou</Typography.Text>
      </Divider>

      <Form<Campos> layout="vertical" onFinish={entrar} requiredMark={false} disabled={enviando}>
        <Form.Item
          name="email"
          label="E-mail"
          rules={[
            {
              required: true,
              type: 'email',
              message: 'Informe um e-mail válido.',
            },
          ]}
        >
          <Input
            autoComplete="email"
            inputMode="email"
            size="large"
            /*
             * A etiqueta mora dentro do campo, como na Voa: fora dele, ela
             * competiria com o rótulo pela mesma linha e empurraria o
             * formulário para baixo.
             */
            suffix={anterior === 'email' ? <EtiquetaDaUltimaVez /> : undefined}
          />
        </Form.Item>

        <Form.Item
          name="password"
          label="Senha"
          rules={[{ required: true, message: 'Informe sua senha.' }]}
          /*
           * O "esqueci minha senha" entra como `extra`, colado ao campo a que
           * pertence. Solto no rodapé, ele virava um terceiro link a competir
           * com "criar conta" - e quem esqueceu a senha só procura essa saída
           * depois de olhar para o campo dela.
           */
          extra={
            <Flex justify="flex-end">
              <Link to="/esqueci-a-senha">Esqueci minha senha</Link>
            </Flex>
          }
        >
          <Input.Password autoComplete="current-password" size="large" />
        </Form.Item>

        <Button type="primary" htmlType="submit" size="large" block loading={enviando}>
          Entrar
        </Button>
      </Form>

      <Typography.Text type="secondary" style={{ textAlign: 'center' }}>
        Ainda não tem conta? <Link to={comDestino('/criar-conta', destino)}>Criar conta</Link>
      </Typography.Text>
    </Flex>
  );
}
